/**
 * SessionTimer - 세션 만료 카운트다운 타이머
 *
 * MM:SS 형식으로 남은 시간을 표시합니다.
 * 5분 미만이면 노란색 경고, 만료 시 표시만 수행
 *
 * @see Issue #220 - 세션 만료 타이머 UI
 */

import { Clock, AlertTriangle } from 'lucide-react';

interface SessionTimerProps {
  /** 남은 시간 (초) */
  remainingSeconds: number;
  /** 경고 상태 (5분 미만) */
  isWarning: boolean;
  /** 만료 여부 */
  isExpired: boolean;
}

/** 초를 MM:SS 형식으로 변환 */
function formatTime(seconds: number): string {
  if (seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function SessionTimer({
  remainingSeconds,
  isWarning,
  isExpired,
}: SessionTimerProps) {
  if (isExpired) {
    return (
      <div className="flex items-center gap-1.5 text-destructive">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-sm font-semibold tabular-nums">
          00:00
        </span>
      </div>
    );
  }

  if (isWarning) {
    return (
      <div className="flex items-center gap-1.5 text-yellow-600 dark:text-yellow-400">
        <AlertTriangle className="h-4 w-4 animate-pulse" />
        <span className="text-sm font-semibold tabular-nums">
          {formatTime(remainingSeconds)}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 text-foreground">
      <Clock className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-medium tabular-nums">
        {formatTime(remainingSeconds)}
      </span>
    </div>
  );
}
